/**
 * 认证API服务
 */

import axios, { AxiosResponse } from 'axios';
import { LoginCredentials, RegisterCredentials, AuthResponse, User } from '../types/auth';
import { API_BASE_URL } from '../config/api';

// 使用智能API配置

// 创建axios实例
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器：添加认证token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 响应拦截器：处理认证错误
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // 如果收到401错误（包括账号被禁用）
    if (error.response?.status === 401) {
      const errorCode = error.response?.data?.error?.code;
      
      // 处理账号被禁用或认证失败的情况
      if (errorCode === 'ACCOUNT_DISABLED' || 
          errorCode === 'INVALID_TOKEN' || 
          errorCode === 'USER_NOT_FOUND') {
        // 清除本地存储的认证信息
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        
        // 重定向到登录页面
        window.location.href = '/auth/login';
        
        // 显示错误消息
        if (errorCode === 'ACCOUNT_DISABLED') {
          alert('您的账号已被禁用，请联系管理员');
        } else {
          alert('登录已过期，请重新登录');
        }
      }
    }
    
    return Promise.reject(error);
  }
);

/**
 * 用户注册
 */
export async function register(credentials: RegisterCredentials): Promise<AuthResponse['data']> {
  try {
    const response: AxiosResponse<AuthResponse> = await api.post('/api/v1/auth/register', credentials);
    return response.data.data;
  } catch (error: any) {
    const message = error.response?.data?.error?.message || '注册失败';
    throw new Error(message);
  }
}

/**
 * 用户登录
 */
export async function login(credentials: LoginCredentials): Promise<AuthResponse['data']> {
  try {
    const response: AxiosResponse<AuthResponse> = await api.post('/api/v1/auth/login', credentials);
    return response.data.data;
  } catch (error: any) {
    const message = error.response?.data?.error?.message || '登录失败';
    throw new Error(message);
  }
}

/**
 * 获取用户信息
 */
export async function getProfile(): Promise<User> {
  try {
    const response = await api.get('/api/v1/me/profile');
    return response.data.data;
  } catch (error: any) {
    const message = error.response?.data?.error?.message || '获取用户信息失败';
    throw new Error(message);
  }
}

/**
 * 用户登出
 */
export async function logout(): Promise<void> {
  try {
    await api.post('/api/v1/auth/logout');
  } catch (error) {
    // 即使服务端登出失败，前端也应该清除本地状态
    console.warn('服务端登出失败，但会继续清除本地状态');
  }
}

/**
 * 验证token是否有效
 */
export async function validateToken(): Promise<boolean> {
  try {
    await getProfile();
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * 忘记密码 - 发送重置链接
 */
export async function forgotPassword(email: string): Promise<{ message: string }> {
  try {
    const response = await api.post('/api/v1/auth/forgot-password', { email });
    return response.data.data;
  } catch (error: any) {
    const message = error.response?.data?.error?.message || '发送重置链接失败';
    throw new Error(message);
  }
}

/**
 * 重置密码
 */
export async function resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
  try {
    const response = await api.post('/api/v1/auth/reset-password', { token, newPassword });
    return response.data.data;
  } catch (error: any) {
    const message = error.response?.data?.error?.message || '密码重置失败';
    throw new Error(message);
  }
}

/**
 * 更新用户密码（需要登录）
 */
export async function updatePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
  try {
    const response = await api.put('/api/v1/me/password', { currentPassword, newPassword });
    return response.data.data;
  } catch (error: any) {
    const message = error.response?.data?.error?.message || '密码更新失败';
    throw new Error(message);
  }
} 